import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import { ImpactPartner } from "../models/ImpactPartner";
import { PartnerMessage } from '../models/PartnerMessage';
import { ShoppingCart } from '../models/ShoppingCart';
import { OrderResponseDto } from '../models/OrderResponseDto';
import CheckoutService from '../services/CheckoutService';
import * as AuthorizationStore from '../store/Authorization';
import { toast } from 'react-toastify';
import ImpactPartnerService from '../services/ImpactPartnerService';

export interface IState {
    currentImpactPartner: ImpactPartner;
    availableImpactPartners: ImpactPartner[];
    impactPartnersMessages: PartnerMessage[];
    currentShoppingCart: ShoppingCart;
    processedImpactPartners: ImpactPartner[];
    sendingOrderResponse: boolean;
}

const unloadedState: IState = {
    currentImpactPartner: undefined,
    availableImpactPartners: [],
    impactPartnersMessages: [],
    currentShoppingCart: undefined,
    processedImpactPartners: [],
    sendingOrderResponse: false
};

export enum Actions {
    SetShoppingCart = "SetShoppingCart",
    SetImpactPartner = "SetImpactPartner",
    SetAvailableImpactPartners = "SetAvailableImpactPartners",
    SetProcessedImpactPartner = "SetProcessedImpactPartner",
    SetImpactPartnersMessages = "SetImpactPartnersMessages",
    BeginSendOrderResponse = "BeginSendOrderResponse",
    EndSendOrderResponse = "EndSendOrderResponse"
}

interface SetShoppingCartAction {
    type: Actions.SetShoppingCart;
    shoppingCart: ShoppingCart;
}

interface SetAvailableImpactPartnersAction {
    type: Actions.SetImpactPartner;
    impactPartner: ImpactPartner;
}

interface SetImpactPartnerAction {
    type: Actions.SetAvailableImpactPartners;
    impactPartners: ImpactPartner[];
}

interface SetProcessedImpactPartnerAction {
    type: Actions.SetProcessedImpactPartner;
    impactPartner: ImpactPartner;
}

interface SetImpactPartnersMessagesAction {
    type: Actions.SetImpactPartnersMessages;
    partnersMessages: PartnerMessage[];
}

interface SetBeginSendOrderResponseAction {
    type: Actions.BeginSendOrderResponse;
}

interface SetEndSendOrderResponseAction {
    type: Actions.EndSendOrderResponse;
}

type KnownAction = SetImpactPartnerAction | SetAvailableImpactPartnersAction
    | SetShoppingCartAction | SetProcessedImpactPartnerAction
    | SetImpactPartnersMessagesAction | SetBeginSendOrderResponseAction
    | SetEndSendOrderResponseAction;

const checkLocalStorageAvailability = () => {
    var test = 'test';
    try {
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        return true;
    } catch (e) {
        return false;
    }
}

const getCartItemsFromLocalStorage = (userId: string) => {

    if (checkLocalStorageAvailability()) {

        try {

            var value = localStorage.getItem(`checkout_cart_${userId}`);
            var items = JSON.parse(value);

            return items || [];

        } catch (e) {
            console.log('could not parse cart items from local storage');
        }
    }

    return [];
}

export const actionCreators = {
    setShoppinCart: (cart: ShoppingCart): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            //const cart = getCartItemsFromLocalStorage(state.authorization.currentUser.UserId);

            dispatch({
                type: Actions.SetShoppingCart,
                shoppingCart: cart
            });
        }
    },

    setImpactPartner: (impactPartner: ImpactPartner): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.SetImpactPartner,
                impactPartner: impactPartner
            });
        }
    },

    setAvailableImpactPartners: (impactPartners: ImpactPartner[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.SetAvailableImpactPartners,
                impactPartners: impactPartners
            });
        }
    },

    setProcessedImpactPartner: (impactPartner: ImpactPartner): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.SetProcessedImpactPartner,
                impactPartner: impactPartner
            });
        }
    },

    setImpactPartnersMessages: (partnersMessages: PartnerMessage[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {

        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.SetImpactPartnersMessages,
                partnersMessages: partnersMessages
            });
        }
    },

    sendOrderResponse: (orderResponse: OrderResponseDto): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.BeginSendOrderResponse
            });

            // Here you would typically make an API call to send the order response
            console.log('Order response sent:', orderResponse);
            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();

            var partnerResponse = await ImpactPartnerService.get(partner.Id);
            var cartPartner = partnerResponse.value;
            const response = await CheckoutService.sendOrderResponse(cartPartner, orderResponse);

            if (!response.hasErrors) {
                toast.success("Order response summary sent successfully!");
            } else {
                toast.error("Failed to send the order response summary!");
                console.log("SendOrderResponse", response.errorMessage);
            }

            dispatch({
                type: Actions.EndSendOrderResponse
            });
        }
    }
}

export const reducer: Reducer<IState> = (state: IState | undefined, incomingAction: Action): IState => {
    if (state === undefined) {
        return unloadedState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case Actions.SetShoppingCart: {
            return {
                ...state,
                currentShoppingCart: action.shoppingCart
            };
        }

        case Actions.SetImpactPartner: {

            return {
                ...state,
                currentImpactPartner: action.impactPartner
            };
        }

        case Actions.SetAvailableImpactPartners: {

            return {
                ...state,
                availableImpactPartners: action.impactPartners,
                processedImpactPartners: action.impactPartners
            };
        }

        case Actions.SetProcessedImpactPartner: {
            var newState = { ...state };

            newState.processedImpactPartners = newState.processedImpactPartners.filter(x => x.Id !== action.impactPartner.Id);

            return newState;
        }

        case Actions.SetImpactPartnersMessages: {

            return {
                ...state,
                impactPartnersMessages: action.partnersMessages
            };
        }

        case Actions.BeginSendOrderResponse: {
            return {
                ...state,
                sendingOrderResponse: true
            };
        }

        case Actions.EndSendOrderResponse: {
            return {
                ...state,
                sendingOrderResponse: false
            };
        }

        default: return state;
    }
}